/**
 * FIT field numbers for the three message types we read and write, plus small typed
 * accessors over a decoded record. Field numbers were verified empirically against the
 * fixtures (see IMPLEMENTATION_PLAN.md "Verified facts"); do not guess new ones.
 */
import { FieldNotInDefinitionError } from '../codec/errors'
import type { DataRecord, FieldValue } from '../codec/types'

export const GLOBAL = {
  fileId: 0,
  session: 18,
  lap: 19,
  record: 20,
  length: 101,
  bestEffort: 113, // global-113 PR/best-effort records, dropped when any length changes
} as const

/** file_id message (global 0). */
export const FID = {
  manufacturer: 1,
  product: 2,
} as const

/** record message (global 20) — the 1 Hz sample stream. */
export const REC = {
  timestamp: 253,
  heartRate: 3,
} as const

/** session.sport / sub_sport values that identify a pool (lap) swim. */
export const SPORT_SWIMMING = 5
export const SUB_SPORT_LAP_SWIMMING = 17

/** length message (global 101). */
export const LEN = {
  timestamp: 253,
  startTime: 2,
  totalElapsed: 3,
  totalTimer: 4,
  messageIndex: 254,
  totalStrokes: 5,
  avgSpeed: 6,
  totalCalories: 11,
  event: 0,
  eventType: 1,
  swimStroke: 7,
  avgSwimmingCadence: 9,
  lengthType: 12,
} as const

/** lap message (global 19). */
export const LAP = {
  timestamp: 253,
  startTime: 2,
  totalElapsed: 7,
  totalTimer: 8,
  totalDistance: 9,
  totalCycles: 10,
  messageIndex: 254,
  totalCalories: 11,
  firstLengthIndex: 35,
  numLengths: 32,
  numActiveLengths: 40,
  enhAvgSpeed: 110,
  enhMaxSpeed: 111,
  avgSpeedLegacy: 13, // fill-all only
  maxSpeedLegacy: 14, // fill-all only
  avgStrokeDistance: 37, // fill-all only
  swolf: 73,
  strokesPerLengthX10: 90,
  swimStroke: 38, // rippled from the member lengths when they share one stroke
  avgHeartRate: 15,
  maxHeartRate: 16,
} as const

/** session message (global 18). */
export const SES = {
  timestamp: 253,
  startTime: 2,
  totalElapsed: 7, // wall clock incl. pauses — NEVER recomputed
  totalTimer: 8,
  totalDistance: 9,
  totalCycles: 10,
  messageIndex: 254,
  poolLength: 44,
  numLengths: 33,
  numActiveLengths: 47,
  enhAvgSpeed: 124,
  enhMaxSpeed: 125,
  avgSpeedLegacy: 14, // fill-all only
  maxSpeedLegacy: 15, // fill-all only
  avgStrokeDistance: 42, // fill-all only
  strokesPerLengthX10: 79,
  swolf: 80,
  swimStroke: 43, // rippled from the active lengths when they share one stroke
  avgHeartRate: 16,
  maxHeartRate: 17,
  poolLengthUnit: 45, // display_measure enum: 0 metric, 1 statute (yards); 255/invalid → metric
  sport: 5,
  subSport: 6,
} as const

/** length_type values. */
export const LENGTH_TYPE = { idle: 0, active: 1 } as const

/** swim_stroke enum. `unset` (255, "not recorded") is the real state of every raw fixture. */
export const SWIM_STROKE = {
  freestyle: 0,
  backstroke: 1,
  breaststroke: 2,
  butterfly: 3,
  drill: 4,
  mixed: 5,
  im: 6,
  unset: 255,
} as const

/** "value not recorded" sentinels by width, used to detect and write invalid fields. */
export const INVALID = {
  uint8: 0xff,
  uint16: 0xffff,
  uint32: 0xffffffff,
} as const

function fieldIndex(record: DataRecord, fieldDefNum: number): number {
  return record.def.fields.findIndex((f) => f.fieldDefNum === fieldDefNum)
}

/** Raw value of a field, or undefined if the field is absent from this record's definition. */
export function getField(record: DataRecord, fieldDefNum: number): FieldValue | undefined {
  const i = fieldIndex(record, fieldDefNum)
  return i < 0 ? undefined : record.values[i]
}

/** Scalar numeric value of a field, or undefined if absent / not a scalar number. */
export function getNumber(record: DataRecord, fieldDefNum: number): number | undefined {
  const v = getField(record, fieldDefNum)
  return typeof v === 'number' ? v : undefined
}

/**
 * Write a scalar field and mark the record dirty. Throws if the field is not present in the
 * record's definition — we never add fields to a message the watch did not already emit.
 */
export function setNumber(record: DataRecord, fieldDefNum: number, value: number): void {
  const i = fieldIndex(record, fieldDefNum)
  if (i < 0) throw new FieldNotInDefinitionError(record.def.globalMessageNumber, fieldDefNum)
  record.values[i] = value
  record.dirty = true
}
